/* eslint-disable no-param-reassign */
/* eslint-disable no-multi-assign */

const fullscreenEnabled
  = document.fullscreenEnabled
  /* Standard syntax */ || document.webkitFullscreenEnabled
  /* Safari */ || document.msFullscreenEnabled; /* IE11 */

export default function initVideos() {
  // eslint-disable-next-line no-use-before-define
  const process = process;
  const squareVideos = window.matchMedia('screen and (max-width: 600px)');
  squareVideos.addListener(updateVideoSources);
  const videoContainers = Array.from(
    document.querySelectorAll('[class*="video-container"]')
  );

  function updateVideoSources() {
    videoContainers.forEach(videoContainer => {
      const video = videoContainer.querySelector('video');
      if (video.src) updateVideoSrc(video);
    });
  }

  const videoObserver = new IntersectionObserver(updateVideo, {
    threshold: 0.5,
  });
  const srcObserver = new IntersectionObserver(updateSrc);

  function updatePoster(video) {
    const fileName = video.dataset.videoFilename;
    const isSquare = squareVideos.matches;
    const path = `${process.pathPrefix}static/posters/${fileName}-poster${
      isSquare ? '_square' : ''
    }.jpg?ts=${Date.now()}`;
    video.setAttribute('poster', path);
  }

  videoContainers.forEach(videoContainer => {
    const controls = videoContainer.querySelector('[class*="controls"]');
    const video = videoContainer.querySelector('video');
    if (controls) initControls(video, videoContainer);
    if (video.dataset.videoPoster === 'true') updatePoster(video);
    videoObserver.observe(video);
    srcObserver.observe(video);
  });

  function updateVideo(entries) {
    entries.forEach(entry => {
      const obsVideo = entry.target;
      const obsVideoContainer = obsVideo.parentElement;
      if (
        entry.isIntersecting
        && obsVideoContainer.dataset.videoStatus !== 'ended'
        && obsVideoContainer.dataset.videoObserverStatus !== 'paused'
      ) obsVideo.play();
      else {
        const obsTatus = obsVideoContainer.dataset.videoStatus || 'playing';
        obsVideoContainer.dataset.videoObserverStatus = obsTatus;
        obsVideo.pause();
      }
    });
  }

  // lazely updating video src for performance
  function updateSrc(entries) {
    entries.forEach(entry => {
      const srcVideo = entry.target;
      if (entry.isIntersecting && !srcVideo.src) updateVideoSrc(srcVideo);
    });
  }

  function updateVideoSrc(video) {
    const fileName = video.dataset.videoFilename;
    const isSquare = squareVideos.matches;
    const path = `${process.pathPrefix}static/videos/${fileName}${
      isSquare ? '_square' : ''
    }.mp4?ts=${Date.now()}`;
    video.oncanplay = () => {
      video.dataset.videoVisible = 'true';
    };
    video.setAttribute('src', path);
  }
}

const hoverTime = 1600;

function initControls(video, videoContainer) {
  const tc = videoContainer.querySelector('[class*="__tc"]');
  const tds = videoContainer.querySelectorAll('[class*="__td"]');
  const progressBar = videoContainer.querySelector('[class*="__pb"]');
  const indicator = videoContainer.querySelector('[class*="__pi"]');
  const seek = videoContainer.querySelector('[class*="__seek"]');
  const seekTooltip = videoContainer.querySelector('[class*="__pt"]');
  const audioBtn = videoContainer.querySelector(
    '[class*="-controls"] .video-icon-audio'
  );
  const fullscreenBtn = videoContainer.querySelector(
    '[class*="-controls"] [class*="__fullscreen"]'
  );

  if (fullscreenBtn) {
    if (!fullscreenEnabled) fullscreenBtn.style.display = 'none';
    else {
      fullscreenBtn.onclick = ev => {
        ev.stopPropagation();
        if (videoContainer.classList.contains('video-fullscreen')) videoContainer.classList.remove('video-fullscreen');
        else videoContainer.classList.add('video-fullscreen');
        if (document.fullscreenElement) exitFullscreen(videoContainer);
        else enterFullScreen(videoContainer);
      };
      document.addEventListener('fullscreenchange', () => {
        fullscreenBtn
          .querySelector('.video-icon-fullscreen')
          .classList.toggle('fullscreen');
      });
    }
  }

  videoContainer.onclick = () => {
    if (video.muted && videoContainer.dataset.videoType !== 'no-sound') toggleMuted(video);
    else togglePlay(video);
  };

  function toggleMuted(currVideo) {
    currVideo.muted = !currVideo.muted;
    currVideo.play();
    videoContainer.dataset.videoStatus = currVideo.paused
      ? 'paused'
      : 'playing';
  }

  if (audioBtn) {
    audioBtn.onclick = ev => {
      ev.stopPropagation();
      toggleSound();
    };
  }

  function toggleSound() {
    video.muted = !video.muted;
    audioBtn.classList.toggle('muted');
  }

  let hoverTO = null;

  videoContainer.onmouseenter = () => {
    setHoverTimeout();
  };

  videoContainer.onmousemove = () => {
    setHoverTimeout();
  };

  function setHoverTimeout() {
    clearTimeout(hoverTO);
    videoContainer.dataset.videohover = 'true';
    hoverTO = setTimeout(() => {
      videoContainer.dataset.videohover = 'false';
    }, hoverTime);
  }

  videoContainer.onmouseleave = () => {
    videoContainer.dataset.videohover = 'false';
  };

  function togglePlay(currVideo) {
    if (currVideo.paused) {
      currVideo.play();
      setHoverTimeout();
    }
    else currVideo.pause();
    videoContainer.dataset.videoStatus = currVideo.paused
      ? 'paused'
      : 'playing';
  }

  if (video.duration) setTimeout(updateSeekBarInfo, 100);
  else {
    video.onloadedmetadata = () => {
      updateSeekBarInfo();
    };
  }

  function updateSeekBarInfo() {
    const videoDuration = formatTime(Math.round(video.duration));

    tds.forEach(td => {
      td.innerHTML = videoDuration;
      td.onclick = () => {
        video.play();
      };
    });
    seek.setAttribute('max', Math.round(video.duration));
    progressBar.setAttribute('max', Math.round(video.duration));
  }

  video.ontimeupdate = () => {
    const prec
      = (Math.floor(video.currentTime) / Math.round(video.duration)) * 100;
    const time = formatTime(Math.round(video.currentTime));
    tc.innerText = time;
    seek.value = Math.floor(video.currentTime);
    progressBar.style.width = `${prec}%`;
    indicator.style.left = `${prec}%`;
    indicator.dataset.tc = time;
  };

  video.onended = () => {
    if (!video.muted && video.loop) return;

    const fader = videoContainer.querySelector('[class*="fader"]');

    fader.ontransitionend = () => {
      video.currentTime = 0;
      videoContainer.dataset.videoStatus = 'ended';
    };

    videoContainer.dataset.videoStatus = 'fading';
  };

  video.onpause = () => {
    if (!video.muted) videoContainer.dataset.videoStatus = 'paused';
  };
  video.onplaying = () => {
    if (!video.muted) videoContainer.dataset.videoStatus = 'playing';
  };

  let dragPlayingState = 'playing';

  seek.addEventListener('mousemove', updateSeekTooltip);
  seek.addEventListener('mousedown', () => {
    dragPlayingState = video.paused ? 'playing' : 'paused';
    video.pause();
  });
  seek.addEventListener('mouseup', () => {
    if (dragPlayingState === 'playing') video.play();
  });
  seek.addEventListener('input', skipAhead);

  // updateSeekTooltip uses the position of the mouse on the progress bar to
  // roughly work out what point in the video the user will skip to if
  // the progress bar is clicked at that point
  function updateSeekTooltip(event) {
    // Clamp number between two values with the following line:
    const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
    const max = parseInt(event.target.getAttribute('max'), 10);
    const skipTo = clamp(
      Math.round((event.offsetX / event.target.clientWidth) * max),
      0,
      max
    );
    const prec = clamp(
      (Math.floor(skipTo) / Math.round(video.duration)) * 100,
      0,
      100
    );

    const t = formatTime(skipTo);
    seek.dataset.seek = skipTo;
    if (prec >= 0 && prec <= 100) {
      seekTooltip.textContent = t;
      seekTooltip.style.left = `${prec}%`;
    }
  }

  // skipAhead jumps to a different point in the video when
  // the progress bar is clicked
  function skipAhead(event) {
    const skipTo = event.target.dataset.seek
      ? event.target.dataset.seek
      : event.target.value;
    video.currentTime = skipTo;
    progressBar.value = skipTo;
    seek.value = skipTo;
  }

  function formatTime(timeInSeconds) {
    const pad = (num, size) => `000${num}`.slice(size * -1);
    const time = parseFloat(timeInSeconds).toFixed(3);
    const minutes = Math.floor(time / 60) % 60;
    const seconds = Math.floor(time - minutes * 60);

    return `${minutes}:${pad(seconds, 2)}`;
  }

  // Function to enable full screen
  function enterFullScreen(element) {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
    else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen(); // Firefox
    }
    else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen(); // Safari
    }
    else if (element.msRequestFullscreen) {
      element.msRequestFullscreen(); // IE/Edge
    }
  }

  // Function to exit full screen
  function exitFullscreen(element) {
    element.requestFullscreen();
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
    else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    }
    else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }

  document.addEventListener('fullscreenchange', event => {
    const el = event.target;
    if (document.fullscreenElement) el.classList.add('video-fullscreen');
    else el.classList.remove('video-fullscreen');
  });
}
